import { useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import baseUrl from "../../../api/baseUrl";
import MessageLengthHint from "./MessageLengthHint";

export type ClinicWhatsAppTemplate = {
  placeholders: string[];
  defaultBody: string;
  active: { body: string; approvedAt?: string } | null;
  pending: {
    body: string;
    status: string;
    rejectionReason?: string;
    submittedAt?: string;
  } | null;
};

export type WhatsAppTemplateRow = {
  type: string;
  label: string;
  configured: boolean;
  /** A Meta-approved Care32 template exists (with or without token). */
  available?: boolean;
  example: string;
  noToken: { configured: boolean } | null;
  clinic?: ClinicWhatsAppTemplate | null;
};

function errorMessageFrom(error: any, fallback: string) {
  return error?.response?.data?.message || fallback;
}

const PENDING_LABEL: Record<string, string> = {
  rejected: "Rejected by WhatsApp",
  paused: "Paused by WhatsApp",
  disabled: "Disabled by WhatsApp",
};

/**
 * One WhatsApp message type. The clinic can write its own wording (like SMS);
 * it is sent to WhatsApp for approval and used once approved. Until then the
 * Care32 wording (or the clinic's previous approved wording) is sent.
 */
export default function WhatsAppTemplateCard({
  template,
  freeformAllowed,
  onChanged,
}: {
  template: WhatsAppTemplateRow;
  freeformAllowed: boolean;
  onChanged: () => void;
}) {
  const clinic = template.clinic;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const usingOwn = Boolean(clinic?.active);
  const currentBody = clinic?.active?.body || clinic?.defaultBody || template.example;
  const ready = usingOwn || Boolean(template.available) || freeformAllowed;
  const pending = clinic?.pending;
  const pendingFailed = pending && ["rejected", "paused", "disabled"].includes(pending.status);

  const startEdit = () => {
    setDraft(pending?.body || currentBody);
    setMessage(null);
    setEditing(true);
  };

  const insertPlaceholder = (key: string) => setDraft((prev) => `${prev}{{${key}}}`);

  const save = () => {
    setSaving(true);
    setMessage(null);
    baseUrl
      .put(`/client/account/whatsapp-templates/${template.type}`, { body: draft })
      .then((res) => {
        setMessage({ ok: true, text: res?.data?.message || "Sent to WhatsApp for approval." });
        setEditing(false);
        onChanged();
      })
      .catch((err) => setMessage({ ok: false, text: errorMessageFrom(err, "Could not save the template.") }))
      .finally(() => setSaving(false));
  };

  const reset = () => {
    if (!window.confirm("Go back to the Care32 wording for this message?")) return;
    setSaving(true);
    setMessage(null);
    baseUrl
      .delete(`/client/account/whatsapp-templates/${template.type}`)
      .then(() => {
        setEditing(false);
        onChanged();
      })
      .catch((err) => setMessage({ ok: false, text: errorMessageFrom(err, "Could not reset the template.") }))
      .finally(() => setSaving(false));
  };

  return (
    <div
      className={`rounded-2xl border p-4 ${
        ready
          ? "border-emerald-200/80 bg-emerald-50/30 dark:border-emerald-800/50 dark:bg-emerald-950/10"
          : "border-slate-200/80 dark:border-slate-700/80"
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {ready ? (
          <CheckCircleOutlineIcon sx={{ fontSize: 18 }} className="text-emerald-600" />
        ) : (
          <RadioButtonUncheckedIcon sx={{ fontSize: 18 }} className="text-slate-400" />
        )}
        <p className="text-sm font-semibold text-slate-800 dark:text-white">{template.label}</p>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            usingOwn
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
              : template.available
              ? "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"
              : "bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300"
          }`}
        >
          {usingOwn ? "Your wording" : template.available ? "Care32 wording" : freeformAllowed ? "Sandbox: uses SMS wording" : "Not set up"}
        </span>
        {!editing && clinic ? (
          <button
            type="button"
            onClick={startEdit}
            className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
          >
            <EditOutlinedIcon sx={{ fontSize: 14 }} />
            Edit
          </button>
        ) : null}
      </div>

      {editing && clinic ? (
        <div className="mt-3 space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
          />
          <MessageLengthHint text={draft} limit={160} unit="WhatsApp message" />
          <div className="flex flex-wrap gap-1">
            {clinic.placeholders.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => insertPlaceholder(key)}
                className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200"
              >
                {`{{${key}}}`}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            WhatsApp must approve new wording first (usually minutes to a few hours). Until then your
            current wording is sent.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving || !draft.trim()}
              className="rounded-xl bg-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            >
              {saving ? "Sending…" : "Save & send for approval"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 dark:border-slate-600 dark:text-slate-300"
            >
              Cancel
            </button>
            {usingOwn || pending ? (
              <button
                type="button"
                onClick={reset}
                disabled={saving}
                className="rounded-xl px-3 py-1.5 text-xs font-semibold text-rose-600 hover:underline"
              >
                Use Care32 wording
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="mt-2 whitespace-pre-line text-xs text-slate-600 dark:text-slate-300">
          {currentBody}
          <MessageLengthHint text={currentBody} limit={160} unit="WhatsApp message" />
        </p>
      )}

      {pending ? (
        <div
          className={`mt-3 rounded-xl px-3 py-2 text-[11px] ${
            pendingFailed
              ? "bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300"
              : "bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          }`}
        >
          <p className="font-semibold">
            {PENDING_LABEL[pending.status] || "New wording waiting for WhatsApp approval"}
          </p>
          <p className="mt-0.5 whitespace-pre-line">{pending.body}</p>
          {pendingFailed && pending.rejectionReason ? (
            <p className="mt-1">Reason: {pending.rejectionReason}</p>
          ) : null}
          {pendingFailed ? <p className="mt-1">Edit the wording and send it again.</p> : null}
        </div>
      ) : null}

      {template.noToken && !usingOwn && template.configured ? (
        <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
          Without token number:{" "}
          {template.noToken.configured ? "approved template" : 'uses "-" for the token'}
        </p>
      ) : null}

      {message ? (
        <p
          className={`mt-2 text-xs ${
            message.ok ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"
          }`}
        >
          {message.text}
        </p>
      ) : null}
    </div>
  );
}
